import { useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { BackLink, Spinner } from '../components/ui';
import { api, ApiError, useMe } from '../lib/api';

/**
 * Administration dashboard (internal tool; labels in French and English only). Protection is
 * enforced by the server on every call — hiding this page would not be a protection.
 */
const L = {
  fr: {
    title: 'Administration',
    denied: 'Accès réservé aux administrateurs.',
    tabs: {
      overview: 'Vue d’ensemble',
      retention: 'Rétention',
      funnel: 'Entonnoir',
      results: 'Résultats',
      reports: 'Signalements',
      users: 'Joueurs',
      lexicons: 'Dictionnaires',
      tournaments: 'Tournois',
      cosmetics: 'Cosmétiques',
      finance: 'Finances',
      audit: 'Journal d’audit',
    },
    definitions: 'Définitions des métriques',
    create: 'Créer',
    accept: 'Accepter',
    reject: 'Rejeter',
    ban: 'Suspendre',
    unban: 'Réactiver',
    resetName: 'Réinitialiser le nom',
    reviewed: 'Vu',
    estimate: 'Estimation — pas un résultat comptable',
    cosmeticsNote: 'Catalogue versionné dans le code ; purement visuel, aucun effet sur le score.',
  },
  en: {
    title: 'Administration',
    denied: 'Administrators only.',
    tabs: {
      overview: 'Overview',
      retention: 'Retention',
      funnel: 'Funnel',
      results: 'Results',
      reports: 'Reports',
      users: 'Players',
      lexicons: 'Dictionaries',
      tournaments: 'Tournaments',
      cosmetics: 'Cosmetics',
      finance: 'Finance',
      audit: 'Audit log',
    },
    definitions: 'Metric definitions',
    create: 'Create',
    accept: 'Accept',
    reject: 'Reject',
    ban: 'Suspend',
    unban: 'Reinstate',
    resetName: 'Reset name',
    reviewed: 'Reviewed',
    estimate: 'Estimate — not an accounting result',
    cosmeticsNote: 'Catalogue versioned in code; purely visual, no effect on score.',
  },
};

type Tab = keyof (typeof L)['fr']['tabs'];

function Json({ value }: { value: unknown }) {
  return <pre className="json">{JSON.stringify(value, null, 2)}</pre>;
}

function Table({ rows }: { rows: Record<string, unknown>[] }) {
  if (!rows || rows.length === 0) return <p className="muted">—</p>;
  const cols = Object.keys(rows[0]!);
  return (
    <div className="table-scroll">
      <table className="data">
        <thead>
          <tr>
            {cols.map((c) => (
              <th key={c}>{c}</th>
            ))}
          </tr>
        </thead>
        <tbody>
          {rows.map((r, i) => (
            <tr key={i}>
              {cols.map((c) => (
                <td key={c}>
                  {typeof r[c] === 'object' && r[c] !== null
                    ? JSON.stringify(r[c])
                    : String(r[c] ?? '')}
                </td>
              ))}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

export function Admin() {
  const { i18n } = useTranslation();
  const l = i18n.language.startsWith('fr') ? L.fr : L.en;
  const { user } = useMe();
  const [tab, setTab] = useState<Tab>('overview');
  const [data, setData] = useState<Record<string, unknown> | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [q, setQ] = useState('');

  const load = useCallback(async () => {
    setData(null);
    setError(null);
    const paths: Record<Tab, string> = {
      overview: '/api/admin/overview',
      retention: '/api/admin/retention',
      funnel: '/api/admin/funnel',
      results: '/api/admin/results',
      reports: '/api/admin/reports',
      users: `/api/admin/users?q=${encodeURIComponent(q)}`,
      lexicons: '/api/admin/lexicons',
      tournaments: '/api/tournaments',
      cosmetics: '/api/admin/cosmetics',
      finance: '/api/admin/finance',
      audit: '/api/admin/audit',
    };
    try {
      setData(await api<Record<string, unknown>>('GET', paths[tab]));
    } catch (e) {
      setError(
        e instanceof ApiError && (e.status === 403 || e.status === 401) ? l.denied : String(e),
      );
    }
  }, [tab, q, l.denied]);

  useEffect(() => {
    void load();
  }, [load]);

  if (user && user.role !== 'admin') {
    return (
      <main className="page">
        <p className="message error">{l.denied}</p>
      </main>
    );
  }

  const act = async (method: string, path: string, body: unknown) => {
    await api(method, path, body);
    await load();
  };

  return (
    <main className="page admin">
      <BackLink />
      <h1>{l.title}</h1>
      <div className="row wrap">
        {(Object.keys(l.tabs) as Tab[]).map((k) => (
          <button
            key={k}
            className={`chip small${tab === k ? ' selected' : ''}`}
            onClick={() => setTab(k)}
          >
            {l.tabs[k]}
          </button>
        ))}
      </div>
      {error && <p className="message error">{error}</p>}
      {!data && !error && <Spinner label="…" />}
      {data && tab === 'overview' && (
        <>
          <Json
            value={{
              users: data.users,
              active: data.active,
              subscriptions: data.subscriptions,
              incidents: data.incidents,
            }}
          />
          <Table rows={data.games as Record<string, unknown>[]} />
          <h2>{l.definitions}</h2>
          <Json value={data.definitions} />
        </>
      )}
      {data && tab === 'retention' && (
        <>
          <Table rows={data.cohorts as Record<string, unknown>[]} />
          <p className="muted small">{String(data.definition)}</p>
        </>
      )}
      {data && tab === 'funnel' && (
        <>
          <Json value={data.funnel} />
          <Json value={data.definitions} />
        </>
      )}
      {data && tab === 'results' && <Table rows={data.results as Record<string, unknown>[]} />}
      {data && tab === 'reports' && (
        <>
          <h2>Words</h2>
          {(data.words as { id: number; lang: string; word: string; comment: string | null }[]).map(
            (w) => (
              <div key={w.id} className="row round-row">
                <span>
                  {w.lang.toUpperCase()} « {w.word} » {w.comment ?? ''}
                </span>
                <span>
                  <button
                    className="btn small primary"
                    onClick={() =>
                      void act('POST', `/api/admin/word-reports/${w.id}`, { status: 'accepted' })
                    }
                  >
                    {l.accept}
                  </button>{' '}
                  <button
                    className="btn small ghost"
                    onClick={() =>
                      void act('POST', `/api/admin/word-reports/${w.id}`, { status: 'rejected' })
                    }
                  >
                    {l.reject}
                  </button>
                </span>
              </div>
            ),
          )}
          <h2>Players</h2>
          {(
            data.players as {
              id: number;
              display_name: string;
              reason: string;
              reported_id: string;
            }[]
          ).map((p) => (
            <div key={p.id} className="row round-row">
              <span>
                {p.display_name} — {p.reason}
              </span>
              <span>
                <button
                  className="btn small ghost"
                  onClick={() =>
                    void act('POST', `/api/admin/users/${p.reported_id}/moderate`, {
                      action: 'reset_name',
                    })
                  }
                >
                  {l.resetName}
                </button>{' '}
                <button
                  className="btn small ghost"
                  onClick={() =>
                    void act('POST', `/api/admin/player-reports/${p.id}`, { status: 'dismissed' })
                  }
                >
                  {l.reject}
                </button>
              </span>
            </div>
          ))}
          <h2>Integrity</h2>
          {(
            data.flags as {
              id: number;
              kind: string;
              display_name: string | null;
              details: unknown;
            }[]
          ).map((f) => (
            <div key={f.id} className="row round-row">
              <span>
                {f.display_name} — {f.kind} {JSON.stringify(f.details)}
              </span>
              <button
                className="btn small ghost"
                onClick={() => void act('POST', `/api/admin/flags/${f.id}/review`, {})}
              >
                {l.reviewed}
              </button>
            </div>
          ))}
        </>
      )}
      {data && tab === 'users' && (
        <>
          <input type="text" value={q} onChange={(e) => setQ(e.target.value)} placeholder="…" />
          {(
            data.users as {
              id: string;
              display_name: string;
              kind: string;
              banned_at: string | null;
              role: string;
            }[]
          ).map((u) => (
            <div key={u.id} className="row round-row">
              <span>
                {u.display_name} · {u.kind} · {u.role} {u.banned_at ? '⛔' : ''}
              </span>
              <span>
                <button
                  className="btn small ghost"
                  onClick={() =>
                    void act('POST', `/api/admin/users/${u.id}/moderate`, {
                      action: u.banned_at ? 'unban' : 'ban',
                    })
                  }
                >
                  {u.banned_at ? l.unban : l.ban}
                </button>
              </span>
            </div>
          ))}
        </>
      )}
      {data && tab === 'lexicons' && <Json value={data} />}
      {data && tab === 'tournaments' && (
        <TournamentAdmin data={data} onCreate={load} label={l.create} />
      )}
      {data && tab === 'cosmetics' && (
        <>
          <p className="muted small">{l.cosmeticsNote}</p>
          <Table rows={data.cosmetics as Record<string, unknown>[]} />
        </>
      )}
      {data && tab === 'finance' && (
        <>
          <p className="message info">{l.estimate}</p>
          <Json value={data} />
        </>
      )}
      {data && tab === 'audit' && <Table rows={data.audit as Record<string, unknown>[]} />}
    </main>
  );
}

function TournamentAdmin({
  data,
  onCreate,
  label,
}: {
  data: Record<string, unknown>;
  onCreate: () => Promise<void>;
  label: string;
}) {
  const [title, setTitle] = useState('');
  const [langs, setLangs] = useState('fr');
  const [days, setDays] = useState(7);
  const [error, setError] = useState<string | null>(null);
  return (
    <>
      <form
        className="card"
        onSubmit={async (e) => {
          e.preventDefault();
          setError(null);
          try {
            const now = Date.now();
            const languages = langs.split('+').map((x) => x.trim());
            await api('POST', '/api/admin/tournaments', {
              title,
              mode: languages.length > 1 ? 'polyglot' : 'classic',
              languages,
              rounds: 3,
              startsAt: new Date(now).toISOString(),
              endsAt: new Date(now + days * 86_400_000).toISOString(),
              timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
            });
            await onCreate();
          } catch (err) {
            setError(String(err));
          }
        }}
      >
        <input
          type="text"
          placeholder="Title"
          value={title}
          onChange={(e) => setTitle(e.target.value)}
          required
          minLength={3}
        />
        <input
          type="text"
          placeholder="fr or fr+en"
          value={langs}
          onChange={(e) => setLangs(e.target.value)}
        />
        <input
          type="number"
          min={1}
          max={30}
          value={days}
          onChange={(e) => setDays(Number(e.target.value))}
        />
        <button className="btn primary small" type="submit">
          {label}
        </button>
        {error && <p className="message error">{error}</p>}
      </form>
      <Table rows={(data.tournaments as Record<string, unknown>[]) ?? []} />
    </>
  );
}
